import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setEditableMessage } from '../../actions/chat/actions';
import { CHATBOT_ID } from '../../constants/chatbotId';
import {
  EReplyTemplateTrackingSource,
  trackReplyTemplateCreateModalOpen,
  trackReplyTemplateEditClick,
  trackReplyTemplateItemClick,
  trackReplyTemplateListModalOpen,
} from '../../containers/ReplyTemplateModalContainer/tracking';
import { EAvailableActions } from '../../repositories/chats/entities/chat/ChatDataInListSchema';
import { currentChatOpponentIdSelector, currentOpenChatSelector, hostTypeSelector } from '../../selectors';
import {
  closeModal,
  openModal,
  selectEditableReplyTemplateId,
  selectIsReplyTemplateEnabled,
  selectIsReplyTemplateInputVisible,
  selectIsReplyTemplateNewEnabled,
  selectReplyTemplateErrorMessage,
  selectReplyTemplateInitialText,
  selectReplyTemplateIsModalOpen,
  selectReplyTemplateLimit,
  selectReplyTemplateModalStep,
  selectReplyTemplatesList,
  selectReplyTemplateStatus,
  setEditableReplyTemplateId,
  setIsReplyTemplateNewEnabled,
  setIsReplyTemplateOnboardingEnabled,
  setModalStep,
  setReplyTemplateInitialText,
} from '../../slices/replyTemplatesSlice';
import {
  createUserReplyTemplateThunk,
  deleteUserReplyTemplateThunk,
  getReplyTemplatesThunk,
  updateUserReplyTemplateThunk,
} from '../../thunks/replyTemplates';
import { EHostType } from '../../types/hostType';
import { EReplyTemplateModalStep } from '../../types/replyTemplates';

const REPLY_TEMPLATES_NEW_KEY = 'isReplyTemplatesOpened';
const REPLY_TEMPLATES_ONBOARDING_KEY = 'isReplyTemplatesOnboardingShown';

export const useReplyTemplateModal = () => {
  const dispatch = useDispatch();

  const isReplyTemplateModalOpen = useSelector(selectReplyTemplateIsModalOpen);
  const isReplyTemplateEnabled = useSelector(selectIsReplyTemplateEnabled);
  const isReplyTemplateNewEnabled = useSelector(selectIsReplyTemplateNewEnabled);
  const isReplyTemplateInputButtonVisible = useSelector(selectIsReplyTemplateInputVisible);

  const replyTemplates = useSelector(selectReplyTemplatesList);
  const replyTemplateModalStep = useSelector(selectReplyTemplateModalStep);
  const replyTemplatesLimit = useSelector(selectReplyTemplateLimit);
  const replyTemplateStatus = useSelector(selectReplyTemplateStatus);
  const replyTemplateErrorMessage = useSelector(selectReplyTemplateErrorMessage);
  const replyTemplateInitialText = useSelector(selectReplyTemplateInitialText);
  const editableReplyTemplateId = useSelector(selectEditableReplyTemplateId);
  const currentChat = useSelector(currentOpenChatSelector);
  const hostType = useSelector(hostTypeSelector);

  const opponentId = useSelector(currentChatOpponentIdSelector);
  const chatId = currentChat?.chatId;
  const availableActions = currentChat?.availableActions;
  const isIframe = hostType === EHostType.Frame;

  React.useEffect(() => {
    const isOnboardingShown = localStorage.getItem(REPLY_TEMPLATES_ONBOARDING_KEY);
    dispatch(setIsReplyTemplateOnboardingEnabled(!isOnboardingShown));
  }, [dispatch]);

  React.useEffect(() => {
    const isNewOpened = localStorage.getItem(REPLY_TEMPLATES_NEW_KEY) && isReplyTemplateEnabled;
    dispatch(setIsReplyTemplateNewEnabled(!isNewOpened));
  }, [dispatch, isReplyTemplateEnabled]);

  const isReplyTemplateAvailable = React.useMemo(() => {
    return Boolean(
      // Если фича-тогл включен и успешный ответ ручки списка шаблонов ответов
      isReplyTemplateEnabled &&
        // Если чат НЕ с Циан-помощник
        chatId !== CHATBOT_ID &&
        // Если в iframe или в чате есть availableActions, проверяем возможность отправлять сообщения (существующий чат)
        // Если в iframe нет availableActions, пропускаем проверку (новый чат)
        ((isIframe && typeof availableActions === 'undefined') ||
          (isIframe && availableActions === null) ||
          availableActions?.includes(EAvailableActions.SendMessage)),
    );
  }, [isReplyTemplateEnabled, chatId, isIframe, availableActions]);

  const openReplyTemplateModal = (source: EReplyTemplateTrackingSource) => {
    if (replyTemplates.length === 0) {
      dispatch(setModalStep(EReplyTemplateModalStep.Create));
      trackReplyTemplateCreateModalOpen({ chatId, opponentId, source });
    } else {
      dispatch(setModalStep(EReplyTemplateModalStep.List));
      trackReplyTemplateListModalOpen({ chatId, opponentId, source });
    }

    dispatch(openModal());

    if (isReplyTemplateNewEnabled) {
      dispatch(setIsReplyTemplateNewEnabled(false));
      localStorage.setItem(REPLY_TEMPLATES_NEW_KEY, '1');
    }
  };

  const openReplyTemplateModalEdit = (id: number) => {
    trackReplyTemplateEditClick({ chatId, opponentId, replyTemplateId: id });
    dispatch(setEditableReplyTemplateId(id));
    dispatch(setModalStep(EReplyTemplateModalStep.Edit));
  };

  const openReplyTemplateModalCreate = (source: EReplyTemplateTrackingSource, text?: string | null) => {
    if (text) {
      dispatch(setReplyTemplateInitialText(text));
    }
    dispatch(setModalStep(EReplyTemplateModalStep.Create));
    dispatch(openModal());
    trackReplyTemplateCreateModalOpen({ chatId, opponentId, source });
  };

  const closeReplyTemplateModal = () => {
    dispatch(closeModal());
  };

  const changeModalStep = (step: EReplyTemplateModalStep) => {
    dispatch(setModalStep(step));
  };

  const chooseReplyTemplate = (id: number) => {
    const template = replyTemplates.find(template => template.id === id);
    const chatId = currentChat?.chatId;

    if (template && chatId) {
      dispatch(
        setEditableMessage({
          chatId: currentChat?.chatId,
          text: replyTemplates.find(template => template.id === id)?.text || '',
        }),
      );
      trackReplyTemplateItemClick({ chatId, opponentId, replyTemplateId: id });
    }
    dispatch(closeModal());
  };

  const createReplyTemplate = (text: string, name?: string) => {
    dispatch(createUserReplyTemplateThunk({ name, text }));

    dispatch(setIsReplyTemplateOnboardingEnabled(false));
    localStorage.setItem(REPLY_TEMPLATES_ONBOARDING_KEY, '1');
  };

  const editReplyTemplate = (id: number, text: string, name?: string) => {
    dispatch(updateUserReplyTemplateThunk({ id, text, name }));
  };

  const deleteReplyTemplate = (id: number) => {
    dispatch(deleteUserReplyTemplateThunk({ id }));
  };

  const getReplyTemplates = () => {
    dispatch(getReplyTemplatesThunk());
  };

  const editableReplyTemplate = React.useMemo(() => {
    return replyTemplates.find(template => template.id === editableReplyTemplateId) || null;
  }, [editableReplyTemplateId, replyTemplates]);

  return {
    replyTemplates,
    replyTemplatesLimit,
    isReplyTemplateModalOpen,
    isReplyTemplateEnabled: isReplyTemplateAvailable,
    isReplyTemplateNewEnabled,
    isReplyTemplateInputButtonVisible,
    replyTemplateModalStep,
    editableReplyTemplate,
    replyTemplateStatus,
    replyTemplateErrorMessage,
    replyTemplateInitialText,
    openReplyTemplateModal,
    openReplyTemplateModalEdit,
    openReplyTemplateModalCreate,
    closeReplyTemplateModal,
    changeModalStep,
    createReplyTemplate,
    editReplyTemplate,
    deleteReplyTemplate,
    getReplyTemplates,
    chooseReplyTemplate,
  };
};
